/**
 * Design tokens for the app, on the carro-website design system
 * (`carro-website/src/design/tokens.json`, `docs/design-system.md`).
 *
 * Two layers:
 *
 *   1. Palettes (`ThemePalette`): a cool grey, iris, blue, red, amber and green, each 50–950,
 *      generated in OKLCH so the steps are even to the eye. The website's neutral and accent
 *      colours are exact steps (8 of its 17); the rest (cream, sand, coral, orchid, lilac…) stay
 *      brand colours in `BrandColors`. The comment on each step is its contrast against white.
 *   2. Role tokens (`TokenColors`): what the interface uses, named for the job. Each points at a
 *      palette step. New code uses these.
 *
 * Greys are cool. Iris is the one accent: actions, on and selected states, and focus. The other
 * chromatic brand colours are ornament only. See REBRANDING.md for the recorded exceptions.
 */

/** The website's 17 colours, named after the website token. */
export enum BrandColors {
  canvasWhite = '#ffffff',
  softCream = '#f0eee9',
  hairlineMist = '#e5e7eb',
  silverRule = '#d9d9dd',
  warmSand = '#d9d5cc',
  /** Non-text marks only: 3.0:1 on white. */
  fog = '#93939f',
  /** The lightest grey for text. */
  slateCaption = '#69697c',
  graphiteBody = '#616161',
  pressBlack = '#212121',
  inkwell = '#17171c',
  charcoalDepth = '#2e2e2e',
  coralGlow = '#ff7759',
  irisWash = '#7670c5',
  /** The announcement band; white text on it clears 4.5:1. */
  irisDeep = '#706abb',
  signalBlue = '#4c6ee6',
  lilacMist = '#f0dff3',
  orchid = '#d18ee2',
}

/** The palettes. Steps of 3:1 and above work for borders and icons, 4.5:1 and above for text. */
export const ThemePalette = {
  gray: {
    25: '#fafafc', // 1.04:1 (the page background)
    50: '#f3f3f7', // 1.11:1
    100: BrandColors.hairlineMist, // 1.24:1
    200: BrandColors.silverRule, // 1.41:1
    300: '#bdbdc5', // 1.87:1
    400: BrandColors.fog, // 3.04:1
    500: '#7f7f8d', // 3.95:1
    600: BrandColors.slateCaption, // 5.37:1
    700: '#51525a', // 7.77:1
    800: '#3c3c42', // 10.95:1
    900: '#262629', // 15.09:1
    950: BrandColors.inkwell, // 17.86:1
  },
  iris: {
    50: '#f2f2ff', // 1.11:1
    100: '#e8e8fd', // 1.21:1 (lilac mist leans pink; it stays a brand colour, off the ramp)
    200: '#d2d2f7', // 1.47:1
    300: '#b8b8eb', // 1.89:1
    400: '#9895db', // 2.74:1
    500: BrandColors.irisWash, // 4.29:1
    600: BrandColors.irisDeep, // 4.70:1
    700: '#4e4b7f', // 7.96:1
    800: '#3a385c', // 11.05:1
    900: '#252437', // 15.15:1
    950: '#171721', // 17.78:1
  },
  // Signal blue's hue on the status palettes' lightness steps, for info.
  blue: {
    50: '#eaf0ff', // 1.14:1
    100: '#dfe8fe', // 1.23:1
    200: '#c5d5fe', // 1.47:1
    300: '#a2bcff', // 1.88:1
    400: '#7899f8', // 2.72:1
    500: BrandColors.signalBlue, // 4.49:1
    600: '#4867d5', // 5.03:1
    700: '#3952ab', // 7.07:1
    800: '#2a3a6b', // 10.96:1
    900: '#1c263f', // 15.02:1
    950: '#121826', // 17.73:1
  },
  red: {
    50: '#feebe7', // 1.15:1
    100: '#fee0d9', // 1.24:1
    200: '#fec7ba', // 1.49:1
    300: '#f4a896', // 1.93:1
    400: '#e47d66', // 2.83:1
    500: '#d25033', // 4.25:1
    600: '#cc361e', // 5.10:1
    700: '#a1281b', // 7.41:1
    800: '#602b20', // 11.27:1
    900: '#3a1e17', // 15.24:1
    950: '#231410', // 17.81:1
  },
  amber: {
    50: '#fff2d9', // 1.11:1
    100: '#f5e6ca', // 1.23:1
    200: '#eed0ac', // 1.47:1
    300: '#f8ac3d', // 1.92:1
    400: '#cb9244', // 2.72:1
    500: '#ac7215', // 4.06:1
    600: '#985609', // 5.72:1
    700: '#8f520d', // 6.21:1
    800: '#573411', // 11.03:1
    900: '#352211', // 15.13:1
    950: '#20160e', // 17.76:1
  },
  green: {
    50: '#def7ec', // 1.13:1
    100: '#d5efe4', // 1.21:1
    200: '#b9e0d0', // 1.43:1
    300: '#94ccb5', // 1.82:1
    400: '#5fb193', // 2.56:1
    500: '#0a9672', // 3.74:1
    600: '#068665', // 4.56:1
    700: '#046b51', // 6.51:1
    800: '#1a4738', // 10.49:1
    900: '#162c24', // 14.79:1
    950: '#0f1b17', // 17.66:1
  },
} as const;

const { gray, iris, blue, red, amber, green } = ThemePalette;

/**
 * Status colours. Strong is for text and icons, base for fills, dots and rails, tint for the ground
 * of an alert or badge. Warning base takes ink text, never white.
 */
export const StatusColors = {
  errorStrong: red[700],
  errorBase: red[600],
  errorTint: red[50],
  warningStrong: amber[700],
  warningBase: amber[300],
  warningTint: amber[50],
  successStrong: green[700],
  successBase: green[600],
  successTint: green[50],
  infoStrong: blue[700],
  infoBase: blue[500], // signal blue
  infoTint: blue[100],
};

const signatureGradient = `linear-gradient(to right, ${BrandColors.coralGlow}, ${BrandColors.irisWash}, ${BrandColors.signalBlue})`;
const signatureGradientDiagonal = `linear-gradient(78deg, ${BrandColors.coralGlow}, ${BrandColors.irisWash}, ${BrandColors.signalBlue})`;

/** Role tokens, named for the job. Each points at a palette step. */
export const TokenColors = {
  signatureGradient,

  // Surfaces
  /** The page behind cards and panels: a cool off-white so white surfaces lift off it. */
  bodyBackground: gray[25],
  surfaceCanvas: BrandColors.canvasWhite,
  /** Hover and selected fill. */
  surfaceSubtle: gray[50],
  /** A quiet fill: disabled fields, tracks, inactive tabs. */
  surfaceMuted: gray[100],
  /** A firmer fill: progress tracks, placeholders, dividers drawn as blocks. */
  surfaceStrong: gray[200],
  /** The website's warm band, for brand moments; interface fills use `surfaceSubtle`. */
  surfaceCream: BrandColors.softCream,
  surfaceInk: BrandColors.inkwell,
  /** Behind modals and menus: 20% press black, no blur. */
  scrim: 'rgba(33, 33, 33, 0.2)',

  // Text
  bodyTextPrimary: gray[900],
  bodyTextSecondary: gray[700],
  bodyTextCaption: gray[600],
  /** Non-text marks and disabled text. */
  bodyTextDisabled: gray[400],
  bodyTextOnDark: BrandColors.canvasWhite,
  /** Links take the accent, so they read as actions; they underline on hover. */
  bodyTextLinks: iris[600],
  bodyTextLinksHover: iris[700],

  // Lines
  border: gray[100], // hairline mist: cards, card dividers, tables
  borderMedium: gray[300],
  borderStrong: gray[900],

  // Fields (inputs, selects, search, checkboxes, radios): white, a border that clears 3:1, iris
  // while focused.
  inputBackground: BrandColors.canvasWhite,
  /** A field's edge, empty or filled: 3:1 on white (WCAG 1.4.11), the same tone as `controlBorder`. */
  inputBorder: gray[400],
  inputBorderFocus: iris[600],
  /** Checkbox and radio outlines: they have no fill, so they keep a 3:1 edge. */
  controlBorder: gray[400],
  inputBackgroundDisabled: gray[100],
  inputBorderDisabled: 'transparent',
  inputTextDisabled: gray[400],
  /** The neutral fill of a control: the segmented-control track, a hovered option, a selected chip. */
  controlFill: gray[100],

  // Actions. Iris is the one accent: the primary action, on and selected states, and focus. White
  // on iris 600 clears 4.7:1.
  actionFill: iris[600],
  actionFillHover: iris[700],

  // The accent as text, fill or line: links and active text (accent), its hover (accentHover), a
  // dark accent for text on tints (accentStrong), the selected tint (accentSubtle), a stronger tint
  // (accentMuted) and an accent hairline (accentBorder).
  accent: iris[600],
  accentHover: iris[700],
  accentStrong: iris[800],
  accentSubtle: iris[50],
  accentMuted: iris[100],
  accentBorder: iris[300],
  focusRing: iris[600],
  /** On the code panel, `inkwellWash` and the announcement band, where iris disappears. */
  focusRingOnDark: BrandColors.canvasWhite,

  iconDefault: gray[600],
};

/**
 * The website's washes: the one place hue may cover an area (empty states, sign-in, error pages).
 * One wash per screen, never behind body copy, never on a control.
 */
export const ThemeWashes = {
  sweep: signatureGradientDiagonal,
  iris: `radial-gradient(ellipse 130% 110% at 88% 2% in oklab, ${BrandColors.orchid} 0%, ${BrandColors.lilacMist} 46%, ${BrandColors.canvasWhite} 100%)`,
  coral: `radial-gradient(ellipse 80% 90% at 8% 100% in oklab, rgb(255 119 89 / 92%) 0%, transparent 62%), radial-gradient(ellipse 70% 80% at 96% 4% in oklab, rgb(118 112 197 / 78%) 0%, transparent 66%), linear-gradient(${BrandColors.softCream}, ${BrandColors.softCream})`,
  inkwell: `radial-gradient(ellipse 72% 86% at 14% 6% in oklab, rgb(118 112 197 / 88%) 0%, transparent 68%), radial-gradient(ellipse 66% 78% at 92% 96% in oklab, rgb(76 110 230 / 72%) 0%, transparent 66%), linear-gradient(${BrandColors.inkwell}, ${BrandColors.inkwell})`,
  /** The app's dark twin of inkwell in the sign-in shader's colours: orchid top left, iris bottom right. */
  dusk: `radial-gradient(ellipse 72% 86% at 14% 6% in oklab, rgb(209 142 226 / 72%) 0%, transparent 68%), radial-gradient(ellipse 66% 78% at 92% 96% in oklab, rgb(118 112 197 / 80%) 0%, transparent 66%), linear-gradient(${BrandColors.inkwell}, ${BrandColors.inkwell})`,
  lilac: `radial-gradient(ellipse 92% 86% at 10% 96% in oklab, rgb(209 142 226 / 64%) 0%, transparent 62%), radial-gradient(ellipse 84% 78% at 94% 6% in oklab, rgb(118 112 197 / 40%) 0%, transparent 64%), linear-gradient(${BrandColors.lilacMist}, ${BrandColors.lilacMist})`,
  band: `linear-gradient(to bottom, ${BrandColors.canvasWhite} 0 50%, transparent 50%), ${signatureGradientDiagonal}`,
} as const;

export type ThemeWash = keyof typeof ThemeWashes;

export const ThemeColors = {
  ...BrandColors,
  ...StatusColors,
  ...TokenColors,
};

export type ThemeColor = keyof typeof ThemeColors;

enum ThemeFonts {
  primary = "'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif",
  /** Brand moments at 40px and above only (sign-in, onboarding). */
  display = "'Cormorant', Georgia, 'Times New Roman', serif",
  /** Code, IDs, SKUs and raw values only — never labels or table headers. */
  monospace = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
}

/** 400 and 700 only. */
export const ThemeFontWeights = {
  regular: 400,
  bold: 700,
} as const;

/**
 * The type scale: a size and a line height per step. The interface steps are the app's own, for
 * dense screens (an exception in REBRANDING.md); `lede`, `displayLg` and `title` are the website's
 * `lg`, `5xl` and `5xl-2`. `typography()` builds every variant from these.
 */
export const ThemeText = {
  // Inter: headings and running text
  h1: { size: '32px', leading: '48px' },
  h2: { size: '26px', leading: '40px' },
  h3: { size: '20px', leading: '32px' },
  h4: { size: '16px', leading: '23px' },
  /** H1–H3 below md, where the full steps crowd a phone. */
  h1Sm: { size: '26px', leading: '34px' },
  h2Sm: { size: '22px', leading: '30px' },
  h3Sm: { size: '18px', leading: '26px' },
  lede: { size: '18px', leading: 1.4 },
  body: { size: '16px', leading: '24px' },
  bodySm: { size: '14px', leading: '22px' },
  caption: { size: '12px', leading: '16px' },

  // Inter: single-line controls, where the box sets the height
  control: { size: '16px', leading: '16px' },
  controlSm: { size: '12px', leading: '14px' },
  input: { size: '14px', leading: '24px' },
  chip: { size: '14px', leading: '14px' },
  chipSm: { size: '12px', leading: '12px' },

  // Inter, uppercase: data-table and list column labels, sidebar section labels (an exception to
  // sentence case in REBRANDING.md)
  columnLabel: { size: '11px', leading: '16px' },
  sectionLabel: { size: '12px', leading: '16px' },

  // Inter: the role label inside an avatar ("Retailer", "Supplier") and nowhere else
  micro: { size: '9px', leading: '12px' },

  // Cormorant: brand moments only
  displaySm: { size: '32px', leading: 1.2 },
  display: { size: '40px', leading: 1.2 },
  displayLg: { size: '48px', leading: 1.2 },
  title: { size: '60px', leading: 1 },
} as const;

export type ThemeTextStep = keyof typeof ThemeText;

/** Tracking: −1% on every step, as the website; −2% on the display face's title step. */
export const ThemeTracking = {
  text: '-0.01em',
  title: '-0.02em',
  /** Uppercase labels open up instead: column labels, and the larger section labels a little more. */
  caps: '0.06em',
  capsWide: '0.08em',
} as const;

/** The complete set of radii. In dense tables and lists use `lg`, not `'2xl'`. */
export const ThemeRadii = {
  md: '4px',
  lg: '8px',
  xl: '12px',
  '2xl': '22px',
  full: '9999px',

  button: '9999px',
  /** Half a 44px field: a pill on one line, a soft corner once it grows (a wrapped multi-select). */
  field: '22px',
  tag: '9999px',
  card: '22px',
  image: '22px',
  codePanel: '12px',
  navElement: '8px',
  smallUi: '4px',
} as const;

/** The website's 8px scale. 4 and 12 are allowed inside dense controls only. */
export const ThemeSpacing = {
  4: '4px',
  8: '8px',
  12: '12px',
  16: '16px',
  24: '24px',
  32: '32px',
  40: '40px',
  56: '56px',
  80: '80px',
  128: '128px',
  144: '144px',
  160: '160px',
  176: '176px',
} as const;

/** One ease-out curve; durations named for what moves. Pressable controls scale to 0.97. */
export const ThemeMotion = {
  easeOut: 'cubic-bezier(0.25, 1, 0.5, 1)',
  hover: '150ms',
  press: '160ms',
  swap: '180ms',
  reveal: '220ms',
  panel: '300ms',
  entrance: '500ms',
  hold: '1500ms',
  pressScale: 0.97,
} as const;

export const breakpointValues = {
  xs: 375,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1440,
} as const;

export const ThemeBreakpoints = {
  xs: `max-width: ${breakpointValues.xs}px`,
  sm: `min-width: ${breakpointValues.sm}px`,
  md: `min-width: ${breakpointValues.md}px`,
  lg: `min-width: ${breakpointValues.lg}px`,
  xl: `min-width: ${breakpointValues.xl}px`,
  xxl: `min-width: ${breakpointValues.xxl}px`,
} as const;

const theme = {
  color: ThemeColors,
  palette: ThemePalette,
  font: ThemeFonts,
  fontWeight: ThemeFontWeights,
  text: ThemeText,
  tracking: ThemeTracking,
  radius: ThemeRadii,
  spacing: ThemeSpacing,
  motion: ThemeMotion,
  wash: ThemeWashes,
  breakpoints: ThemeBreakpoints,
};

export default theme;
